import { useEffect } from "react";
import Lenis from "lenis";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";
import { useGSAP } from "@/hooks/useGSAP";

import HowItWorks from "../components/HowItWorks";
import FAQSection from "../components/FAQSection";
import HeroSection from "../components/HeroSection";
import BrandSection from "../components/BrandSection";
import ValueSection from "../components/ValueSection";
import HowToBuy from "../components/HowToBuy";
import AccidentalDamageGrid from "../components/AccidentalDamageGrid";
import AccidentalSimple from "../components/AccidentalSimple";

const FaqData = [
  {
    question: "What does the Accidental Damage Protection cover?",
    answer: "The Plan covers sudden, unforeseen, and unintentional physical damage to the product caused by external events such as impacts, drops, liquid spills, or similar incidents that result in functional or structural impairment. Coverage applies only where there is a clear and verifiable link between the accidental event and the damage.",
  },
  {
    question: "Does this Plan cover manufacturing defects or product failures?",
    answer: "No. Manufacturing defects, inherent product faults, and performance-related issues are not covered under this Plan and remain the responsibility of the manufacturer under the applicable warranty.",
  },
  {
    question: "Is damage caused by pets covered?",
    answer: "Yes, pet-related damage may be covered, provided it is accidental in nature and results in functional or structural impairment of the product.",
  },
  {
    question: "How many times can I use the Care Program?",
    answer: "Customers are eligible to avail the Care Program once per year per product during the active Plan tenure. Any unused service cannot be carried forward to subsequent years or compensated in any form.",
  },
  {
    question: "Does the Care Program include repair or replacement?",
    answer: "No. The Care Program is strictly limited to maintenance and upkeep services. It does not include repair, replacement, or restoration of damaged components.",
  },
];

export default function NestrootAccidental() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    const handleBeforeUnload = () => {
      sessionStorage.setItem("scrollY", String(window.scrollY));
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // 👇 Check if it's a reload (type === 'reload')
    const navEntries = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];
    const isReload = navEntries.length > 0 && navEntries[0].type === "reload";

    if (isReload) {
      const savedScrollY = sessionStorage.getItem("scrollY");
      if (savedScrollY !== null) {
        setTimeout(() => {
          window.scrollTo(0, parseFloat(savedScrollY));
        }, 100); // Wait for Lenis and DOM layout
      }
    }

    return () => {
      lenis.destroy();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-black">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        {/* hero section */}
        <HeroSection image="nestroot-hero-cover.png" />

        {/* brand section */}
        <BrandSection />

        {/* accidental damage grid section */}
        <AccidentalDamageGrid />

        {/* value section */}
        <ValueSection image="nestroot-value-cover.png" />

        {/* simple protection section */}
        <AccidentalSimple />

        {/* claims section */}
        <section className="tw-py-[100px] tw-bg-white">
          <div className="tw-max-w-4xl tw-mx-auto tw-px-6 tw-text-center">
            <h2 
              className="tw-text-4xl md:tw-text-5xl tw-font-medium tw-mb-6 tw-text-black"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              File claims, hassle-free.
            </h2>
            <p 
              className="tw-text-gray-600 tw-text-xl md:tw-text-[22px] tw-leading-relaxed"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Filing a claim with Nestroots is quick and seamless. Just raise a request online — and our team will guide you at every step, ensuring your issue is resolved without delays.
            </p>
          </div>
        </section>


        {/* how it works */}
        <HowItWorks />

        {/* how to buy */}
        <HowToBuy />

        {/* faq section */}
        <FAQSection data={FaqData} link="/nestroots/accidental/tnc" />

        {/* footer */}
        <Footer />
      </main>
    </div>
  );
}
